import { useLocation } from 'react-router-dom';
import { User, Menu, LogOut, Check } from 'lucide-react';
import { useAuth } from '../stores/authStore';
import { useSubscription } from '../providers/SubscriptionProvider';
import { useTeamStore } from '../stores/teamStore';
import NotificationCenter from '../components/NotificationCenter';
import SupportHelp from '../components/support/SupportHelp';
import { useEffect, useRef, useState } from 'react';
import { performLogoutRedirect } from '../utils/logout';
import { oauthAvatarUrl } from '../lib/oauthAvatarUrl';
import { avatarColor, personInitials } from '../lib/generatedAvatar';
import { isCampaignBuilderPath } from './dataRoutePaths';

/** Contacts left and Help share this. Brand radius is rounded-btn. */
const TOP_CHIP =
  'inline-flex items-center gap-1.5 h-9 px-3 whitespace-nowrap rounded-btn border border-neutral-90/15 bg-white text-sm text-neutral-90';

function trialMiddleLabel(access: string, days: number | null): string | null {
  if (access === 'expired') return 'Trial ended';
  if (access !== 'trialing' || days == null) return null;
  if (days <= 0) return 'Trial ends today';
  if (days === 1) return '1 day left';
  return `${days} days left`;
}

const routeLabels: Record<string, string> = {
  '/app': 'Launchpad',
  '/app/knowledge': 'Knowledge',
  '/app/dashboard': 'Dashboard',
  '/app/conversations': 'Conversations',
  '/app/contacts': 'Contacts',
  '/app/sequences': 'Campaigns',
  '/app/outbound/campaigns': 'Campaigns',
  '/app/phone-numbers': 'Phone Numbers',
  '/app/lead-data': 'Lead Data',
  '/app/appointments': 'Appointments',
  '/app/admin': 'Admin',
  '/app/transcripts': 'Transcripts',
  '/app/home-chat': 'AI Chat',
  '/app/settings/calling': 'Settings • Calling',
  '/app/settings/linkedin': 'Settings • LinkedIn',
  '/app/replies': 'Replies',
  '/app/inbound': 'Inbound',
  '/app/inbound/new': 'New inbound',
  '/app/outbound/reply-inbox': 'Replies',
  '/app/outbound/templates': 'Email Templates'
};

const Topbar = ({ onSidebarToggle }: { onSidebarToggle?: () => void; sidebarCollapsed?: boolean }) => {
  const location = useLocation();
  const currentLabel = routeLabels[location.pathname] || '';
  const { user } = useAuth();
  const { entitlements } = useSubscription();
  const contactLimit = entitlements.contactsMonthly;
  const contactRemaining = entitlements.contactsRemaining;
  const trialMiddle = isCampaignBuilderPath(location.pathname, location.search)
    ? trialMiddleLabel(entitlements.access, entitlements.daysRemaining)
    : null;
  // Only users who belong to more than one team ever see the picker, so the
  // single-team case (almost everyone) renders exactly as it did before.
  const { teams, currentTeam, setCurrentTeam } = useTeamStore();

  const displayName = (user?.name || user?.full_name || user?.email || '');
  const displayEmail = (user?.email || '');
  const avatarUrl = oauthAvatarUrl(user);
  const meta = (user?.user_metadata || {}) as Record<string, string | undefined>;
  const initials = personInitials({
    firstName: meta.given_name || meta.first_name,
    lastName: meta.family_name || meta.last_name,
    name: displayName,
    email: displayEmail,
  });
  const markColor = avatarColor(user?.id || displayEmail || displayName || initials);

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false); };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('click', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('click', onDoc); document.removeEventListener('keydown', onEsc); };
  }, []);

  return (
    <header className="relative z-[35] bg-white px-6 py-4 max-lg:py-3 max-lg:pl-[max(0.75rem,env(safe-area-inset-left))] max-lg:pr-[max(1rem,env(safe-area-inset-right))] grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 shrink-0 overflow-visible">
      {/* Breadcrumbs */}
      <div className="flex min-w-0 items-center space-x-2">
        {/* Sidebar Toggle */}
        {onSidebarToggle && (
          <button
            onClick={onSidebarToggle}
            className="mr-2 p-2 max-lg:mr-1 max-lg:min-h-11 max-lg:min-w-11 rounded-btn hover:bg-neutral-90/10 transition-colors inline-flex items-center justify-center shrink-0"
            title="Toggle sidebar"
          >
            <Menu size={18} className="text-neutral-90/60" />
          </button>
        )}
        <h1 className="text-xl max-lg:text-base font-semibold text-neutral-90 truncate">
          {currentLabel}
        </h1>
        {/* Removed trailing version marker */}
      </div>

      <div className="justify-self-center px-2 text-center">
        {trialMiddle && (
          <p className="text-sm text-neutral-90/70 whitespace-nowrap" data-testid="trial-days-topbar">
            {trialMiddle}
          </p>
        )}
      </div>

      {/* Right Side */}
      <div className="flex items-center justify-end gap-2 shrink-0">
        {typeof contactLimit === 'number' && (
          <div
            className={`${TOP_CHIP} hidden md:inline-flex`}
            title="New contacts remaining this month. Research is included for these contacts."
          >
            {contactLimit === 0
              ? 'No contacts'
              : `${(contactRemaining ?? contactLimit).toLocaleString()} contacts left`}
          </div>
        )}
        {contactLimit == null && entitlements.access === 'active' && entitlements.planTier === 'enterprise' && (
          <div className={`${TOP_CHIP} hidden md:inline-flex`}>
            Custom contacts
          </div>
        )}

        <SupportHelp />

        {/* Notifications */}
        <NotificationCenter />

        {/* Profile */}
        <div className="relative" ref={menuRef}>
          <button onClick={() => setMenuOpen(v => !v)} className="flex items-center space-x-3 max-lg:p-0.5">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-medium text-neutral-90">{displayName || '—'}</div>
              <div className="text-xs text-neutral-90/60">{displayEmail || ''}</div>
            </div>
            {avatarUrl ? (
              <img src={avatarUrl} alt={displayName || 'User'} className="w-8 h-8 rounded-full object-cover border border-neutral-90/10" referrerPolicy="no-referrer" />
            ) : (
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[11px] font-semibold select-none"
                style={{ backgroundColor: markColor }}
              >
                {initials || <User size={16} className="text-white" />}
              </div>
            )}
          </button>
          {menuOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white border border-neutral-90/10 rounded-btn shadow-lg overflow-hidden z-50">
              <div className="px-3 py-2 text-xs text-neutral-90/60">
                Signed in as
                <div className="text-neutral-90 truncate">{displayEmail || displayName}</div>
              </div>
              {teams.length > 1 && (
                <>
                  <div className="my-1 h-px bg-neutral-90/10" />
                  <div className="px-3 pt-1 pb-1">
                    <div className="text-xs text-neutral-90/60">Team</div>
                    <div className="text-[11px] text-neutral-90/40">
                      New campaigns are created here
                    </div>
                  </div>
                  {teams.map((team) => {
                    const isActive = team.id === currentTeam?.id;
                    return (
                      <button
                        key={team.id}
                        onClick={() => {
                          if (!isActive) setCurrentTeam(team);
                          setMenuOpen(false);
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-neutral-90/5 text-neutral-90 flex items-center justify-between gap-2"
                      >
                        <span className="truncate">{team.name}</span>
                        {isActive && <Check size={14} className="text-primary-40 shrink-0" />}
                      </button>
                    );
                  })}
                </>
              )}
              <div className="my-1 h-px bg-neutral-90/10" />
                <a
                  href="/app/settings/calling"
                  onClick={() => setMenuOpen(false)}
                  className="block w-full text-left px-3 py-2 text-sm hover:bg-neutral-90/5 text-neutral-90 flex items-center gap-2"
                >
                  Settings
                </a>
              <button
                onClick={async () => { setMenuOpen(false); await performLogoutRedirect(); }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-neutral-90/5 text-red-600 flex items-center gap-2"
              >
                <LogOut size={14} /> Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Topbar; 